import React, { useCallback, useMemo } from 'react';
import Select, { components, createFilter } from 'react-select';
import classnames from 'classnames';
import { ControlBorderColorDefault, ControlBorderRadiusDefault } from '@targetprocess/global-constants';
import { CloseIcon, CaretIcon } from '@targetprocess/icons';
import { Checkbox } from '@targetprocess/checkbox';
import { Third900 } from '@targetprocess/css-variables';

function _classCallCheck(instance, Constructor) {
  if (!(instance instanceof Constructor)) {
    throw new TypeError("Cannot call a class as a function");
  }
}
function _defineProperties(target, props) {
  for (var i = 0; i < props.length; i++) {
    var descriptor = props[i];
    descriptor.enumerable = descriptor.enumerable || false;
    descriptor.configurable = true;
    if ("value" in descriptor) descriptor.writable = true;
    Object.defineProperty(target, _toPropertyKey(descriptor.key), descriptor);
  }
}
function _createClass(Constructor, protoProps, staticProps) {
  if (protoProps) _defineProperties(Constructor.prototype, protoProps);
  if (staticProps) _defineProperties(Constructor, staticProps);
  Object.defineProperty(Constructor, "prototype", {
    writable: false
  });
  return Constructor;
}
function _defineProperty(obj, key, value) {
  key = _toPropertyKey(key);
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }
  return obj;
}
function _extends() {
  _extends = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends.apply(this, arguments);
}
function _inherits(subClass, superClass) {
  if (typeof superClass !== "function" && superClass !== null) {
    throw new TypeError("Super expression must either be null or a function");
  }
  subClass.prototype = Object.create(superClass && superClass.prototype, {
    constructor: {
      value: subClass,
      writable: true,
      configurable: true
    }
  });
  Object.defineProperty(subClass, "prototype", {
    writable: false
  });
  if (superClass) _setPrototypeOf(subClass, superClass);
}
function _getPrototypeOf(o) {
  _getPrototypeOf = Object.setPrototypeOf ? Object.getPrototypeOf.bind() : function _getPrototypeOf(o) {
    return o.__proto__ || Object.getPrototypeOf(o);
  };
  return _getPrototypeOf(o);
}
function _setPrototypeOf(o, p) {
  _setPrototypeOf = Object.setPrototypeOf ? Object.setPrototypeOf.bind() : function _setPrototypeOf(o, p) {
    o.__proto__ = p;
    return o;
  };
  return _setPrototypeOf(o, p);
}
function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;
  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }
  return target;
}
function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};
  var target = _objectWithoutPropertiesLoose(source, excluded);
  var key, i;
  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }
  return target;
}
function _assertThisInitialized(self) {
  if (self === void 0) {
    throw new ReferenceError("this hasn't been initialised - super() hasn't been called");
  }
  return self;
}
function _possibleConstructorReturn(self, call) {
  if (call && (typeof call === "object" || typeof call === "function")) {
    return call;
  } else if (call !== void 0) {
    throw new TypeError("Derived constructors may only return object or undefined");
  }
  return _assertThisInitialized(self);
}
function _toConsumableArray(arr) {
  return _arrayWithoutHoles(arr) || _iterableToArray(arr) || _unsupportedIterableToArray(arr) || _nonIterableSpread();
}
function _arrayWithoutHoles(arr) {
  if (Array.isArray(arr)) return _arrayLikeToArray(arr);
}
function _iterableToArray(iter) {
  if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter);
}
function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}
function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;
  for (var i = 0, arr2 = new Array(len); i < len; i++) arr2[i] = arr[i];
  return arr2;
}
function _nonIterableSpread() {
  throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _toPrimitive(input, hint) {
  if (typeof input !== "object" || input === null) return input;
  var prim = input[Symbol.toPrimitive];
  if (prim !== undefined) {
    var res = prim.call(input, hint || "default");
    if (typeof res !== "object") return res;
    throw new TypeError("@@toPrimitive must return a primitive value.");
  }
  return (hint === "string" ? String : Number)(input);
}
function _toPropertyKey(arg) {
  var key = _toPrimitive(arg, "string");
  return typeof key === "symbol" ? key : String(key);
}

function styleInject(css, ref) {
  if ( ref === void 0 ) ref = {};
  var insertAt = ref.insertAt;

  if (!css || typeof document === 'undefined') { return; }

  var head = document.head || document.getElementsByTagName('head')[0];
  var style = document.createElement('style');
  style.type = 'text/css';

  if (insertAt === 'top') {
    if (head.firstChild) {
      head.insertBefore(style, head.firstChild);
    } else {
      head.appendChild(style);
    }
  } else {
    head.appendChild(style);
  }

  if (style.styleSheet) {
    style.styleSheet.cssText = css;
  } else {
    style.appendChild(document.createTextNode(css));
  }
}

var css = "/*********************************************************************\n* © Copyright IBM Corp. 2024\n*********************************************************************/\n.dropdown-container__dropdown__12-31-0 {\n  font-size: var(--FontSizeDefault, 13px);\n}\n.dropdown-container__dropdown__12-31-0 .dropdown__control {\n      cursor: pointer;\n      border-radius: var(--ControlBorderRadiusDefault, 3px);\n      border: 1px solid var(--ControlBorderColorDefault, rgba(64, 66, 73, 0.2));\n      min-height: 32px;\n      line-height: 24px;\n      overflow: auto;\n      position: static;\n    }\n.dropdown-container__dropdown__12-31-0 .dropdown__control::-webkit-scrollbar {\n    -webkit-appearance: none;\n    width: 7px;\n    height: 7px;\n  }\n.dropdown-container__dropdown__12-31-0 .dropdown__control::-webkit-scrollbar-track {\n    background-color: rgba(160, 160, 160, 0.1);\n  }\n.dropdown-container__dropdown__12-31-0 .dropdown__control::-webkit-scrollbar-thumb {\n    border-radius: 5px;\n    background-clip: padding-box;\n    background-color: rgba(160, 160, 160, 0.7);\n  }\n.dropdown-container__dropdown__12-31-0 .dropdown__control::-webkit-scrollbar-thumb:hover {\n    background-color: rgba(160, 160, 160, 1);\n  }\n.dropdown-container__dropdown__12-31-0 .dropdown__control::-webkit-scrollbar-corner {\n    background-color: rgba(160, 160, 160, 0.1);\n    border-bottom-right-radius: 5px;\n  }\n.dropdown-container__dropdown__12-31-0 .dropdown__control--is-disabled {\n        background-color: var(--ControlColorDisabled, rgba(236, 236, 237, 0.7));\n      }\n.dropdown-container__dropdown__12-31-0 .dropdown__control--is-focused {\n        -webkit-box-shadow: var(--ControlBoxShadowFocusDefault, 0 0 0 3px rgba(0, 126, 255, 0.2));\n                box-shadow: var(--ControlBoxShadowFocusDefault, 0 0 0 3px rgba(0, 126, 255, 0.2));\n        border-color: var(--ControlBorderColorFocusDefault, #007EFF);\n        outline: none;\n      }\n.dropdown-container__dropdown__12-31-0 .dropdown__control--menu-is-open .dropdown__dropdown-indicator {\n            opacity: 1;\n          }\n.dropdown-container__dropdown__12-31-0 .dropdown__indicator-separator {\n      display: none;\n    }\n.dropdown-container__dropdown__12-31-0 .dropdown__input {\n      padding-left: calc(var(--SpaceSM, 12px) - 2px);\n    }\n.dropdown-container__dropdown__12-31-0 .dropdown__value-container {\n      padding: 0 0 2px 2px;\n    }\n/* Input */\n.dropdown-container__dropdown__12-31-0 .dropdown__value-container > div:last-child {\n        margin: 2px 2px 0 0;\n        padding: 0;\n        -webkit-box-align: center;\n            -ms-flex-align: center;\n                align-items: center;\n        display: -webkit-box;\n        display: -ms-flexbox;\n        display: flex;\n      }\n.dropdown-container__dropdown__12-31-0 .dropdown__value-container--is-multi.dropdown__value-container--has-value .dropdown__input {\n            padding-left: 0;\n          }\n.dropdown-container__dropdown__12-31-0 .dropdown__single-value {\n      margin: 0 0 0 var(--SpaceSM, 12px);\n      display: -webkit-box;\n      display: -ms-flexbox;\n      display: flex;\n      -webkit-box-align: center;\n          -ms-flex-align: center;\n              align-items: center;\n    }\n.dropdown-container__dropdown__12-31-0 .dropdown__multi-value {\n      background-color: var(--Second100, #F5F6F6);\n      margin: 2px 2px 0 0;\n    }\n.dropdown-container__dropdown__12-31-0 .dropdown__multi-value__label {\n        font-size: 100%;\n        line-height: var(--ControlHeightDefault, 24px);\n        padding: 0 0 0 calc(var(--SpaceSM, 12px) - 2px);\n      }\n.dropdown-container__dropdown__12-31-0 .dropdown__multi-value__remove {\n        cursor: pointer;\n        opacity: 0.25;\n        padding: 0 var(--SpaceXS, 4px) 0 0;\n        -ms-flex-negative: 0;\n            flex-shrink: 0;\n      }\n.dropdown-container__dropdown__12-31-0 .dropdown__multi-value__remove:hover {\n          opacity: 0.5;\n          background-color: none;\n        }\n.dropdown-container__dropdown__12-31-0 .dropdown__multi-value__remove path {\n          fill: var(--Second500, #404249);\n        }\n.dropdown-container__dropdown__12-31-0 .dropdown__placeholder {\n      margin: 0 2px 0 calc(var(--SpaceSM, 12px) - 2px);\n      text-overflow: ellipsis;\n      white-space: nowrap;\n      overflow: hidden;\n      width: calc(100% - 2px - var(--SpaceSM, 12px) + 2px);\n    }\n.dropdown-container__dropdown__12-31-0 .dropdown__clear-indicator {\n      display: -webkit-box;\n      display: -ms-flexbox;\n      display: flex;\n      -webkit-box-align: center;\n          -ms-flex-align: center;\n              align-items: center;\n      padding: 0;\n      cursor: pointer;\n      opacity: 0.5;\n      -ms-flex-negative: 0;\n          flex-shrink: 0;\n    }\n.dropdown-container__dropdown__12-31-0 .dropdown__clear-indicator:hover {\n        opacity: 1;\n      }\n.dropdown-container__dropdown__12-31-0 .dropdown__indicators {\n      min-width: 32px;\n    }\n.dropdown-container__dropdown__12-31-0 .dropdown__dropdown-indicator {\n      opacity: 0.5;\n      display: -webkit-box;\n      display: -ms-flexbox;\n      display: flex;\n      -webkit-box-align: start;\n          -ms-flex-align: start;\n              align-items: start;\n      padding: 0 var(--SpaceS, 8px);\n    }\n.dropdown-container__dropdown__12-31-0:hover .dropdown__dropdown-indicator {\n        opacity: 1;\n      }\n.dropdown-container--single-line__dropdown__12-31-0 .dropdown__control__is-focused .dropdown__multi-value {\n            max-width: calc(100% - 4px);\n          }\n.dropdown-container--single-line__dropdown__12-31-0 .dropdown__value-container {\n        -ms-flex-wrap: nowrap;\n            flex-wrap: nowrap;\n      }\n.dropdown-container--single-line__dropdown__12-31-0 .dropdown__multi-value {\n        -ms-flex-negative: 0;\n            flex-shrink: 0;\n        max-width: calc(100% - 2px);\n      }\n.dropdown-container--single-line__dropdown__12-31-0 .dropdown__multi-value__label {\n          text-overflow: ellipsis;\n        }\n.dropdown__value-container--hidden-values__dropdown__12-31-0 .dropdown__input {\n      padding-left: 10px !important;\n    }\n.dropdown__multi-hint__dropdown__12-31-0 {\n    font-size: var(--FontSizeLabel, 11px);\n    color: var(--DrBlack, #000000);\n    padding: var(--SpaceXS, 4px) var(--SpaceSM, 12px);\n    background-color: var(--Second200, #ECECED);\n    position: sticky;\n    bottom: 0;\n  }\n.dropdown__option-with-checkbox__dropdown__12-31-0 {\n    width: 100%;\n    display: -webkit-box;\n    display: -ms-flexbox;\n    display: flex;\n    -webkit-box-align: center;\n        -ms-flex-align: center;\n            align-items: center;\n  }\n.dropdown__option-with-checkbox--is-focused__dropdown__12-31-0, .dropdown__option-with-checkbox__dropdown__12-31-0:active {\n      background-color: var(--ActionBackgroundHover, #F9E9AE);\n    }\n.dropdown__option-with-checkbox__label__dropdown__12-31-0 {\n      -webkit-box-flex: 1;\n          -ms-flex-positive: 1;\n              flex-grow: 1;\n      text-overflow: ellipsis;\n      white-space: nowrap;\n      overflow: hidden;\n    }\n.dropdown__option-with-checkbox__checkbox__dropdown__12-31-0 {\n      visibility: hidden;\n      -webkit-box-flex: 0;\n          -ms-flex-positive: 0;\n              flex-grow: 0;\n      -ms-flex-negative: 0;\n          flex-shrink: 0;\n      display: -webkit-box;\n      display: -ms-flexbox;\n      display: flex;\n      -webkit-box-align: center;\n          -ms-flex-align: center;\n              align-items: center;\n    }\n.dropdown__option-with-checkbox__checkbox__dropdown__12-31-0 input {\n        margin: 0;\n      }\n.dropdown__option:hover .dropdown__option-with-checkbox__checkbox__dropdown__12-31-0, .dropdown__group-heading__dropdown__12-31-0:hover .dropdown__option-with-checkbox__checkbox__dropdown__12-31-0 {\n        visibility: visible;\n      }\n.dropdown__option--is-disabled:hover .dropdown__option-with-checkbox__checkbox__dropdown__12-31-0, .dropdown__group-heading--is-disabled__dropdown__12-31-0:hover .dropdown__option-with-checkbox__checkbox__dropdown__12-31-0 {\n        visibility: hidden;\n      }\n.dropdown__option-with-checkbox__checkbox--is-checked__dropdown__12-31-0 {\n        visibility: visible !important;\n      }\n.dropdown__group-heading__dropdown__12-31-0 {\n    display: -webkit-box;\n    display: -ms-flexbox;\n    display: flex;\n    -webkit-box-align: center;\n        -ms-flex-align: center;\n            align-items: center;\n    font-size: var(--FontSizeLabel, 11px);\n    font-weight: var(--FontWeightSemibold, 600);\n    color: var(--DrBlack, #000000);\n    height: 32px;\n    text-transform: uppercase;\n    padding: 0 var(--SpaceSM, 12px);\n  }\n.dropdown__group-heading__dropdown__12-31-0:hover {\n      background-color: var(--ActionBackgroundHover, #F9E9AE);\n      cursor: pointer;\n    }\n.dropdown__group-heading--is-disabled__dropdown__12-31-0:hover {\n      background-color: initial;\n    }\n.dropdown__menu__dropdown__12-31-0 {\n    font-size: var(--FontSizeDefault, 13px);\n    background: var(--BackgroundDefault, #FFFFFF);\n  }\n.dropdown__menu__dropdown__12-31-0 .dropdown__group {\n        padding: 0;\n        border-bottom: 1px solid var(--Second200, #ECECED);\n      }\n.dropdown__menu__dropdown__12-31-0 .dropdown__option {\n        height: 32px;\n        display: -webkit-box;\n        display: -ms-flexbox;\n        display: flex;\n        -webkit-box-align: center;\n            -ms-flex-align: center;\n                align-items: center;\n        padding: 0 var(--SpaceSM, 12px);\n        color: var(--FontColorDefault, #000000);\n        cursor: pointer;\n        outline: none;\n        font-size: var(--FontSizeDefault, 13px);\n      }\n.dropdown__menu__dropdown__12-31-0 .dropdown__option--is-focused, .dropdown__menu__dropdown__12-31-0 .dropdown__option:active {\n          background-color: var(--ActionBackgroundHover, #F9E9AE);\n        }\n.dropdown__menu__dropdown__12-31-0 .dropdown__option--is-selected, .dropdown__menu__dropdown__12-31-0 .dropdown__option--is-selected:hover, .dropdown__menu__dropdown__12-31-0 .dropdown__option--is-selected:active {\n          background-color: var(--ActionBackgroundSelected, #F1CE4A);\n        }\n.dropdown__menu__dropdown__12-31-0 .dropdown__option--is-disabled {\n          background-color: var(--Second200, #ECECED);\n          opacity: 0.5;\n        }\n.dropdown__menu__dropdown__12-31-0 .dropdown__option--is-disabled:hover {\n            background-color: var(--Second200, #ECECED);\n          }\n.dropdown__menu__dropdown__12-31-0 .dropdown__menu-list {\n        padding: 0;\n      }\n.dropdown__menu__dropdown__12-31-0 .dropdown__menu-list::-webkit-scrollbar {\n    -webkit-appearance: none;\n    width: 7px;\n    height: 7px;\n  }\n.dropdown__menu__dropdown__12-31-0 .dropdown__menu-list::-webkit-scrollbar-track {\n    background-color: rgba(160, 160, 160, 0.1);\n  }\n.dropdown__menu__dropdown__12-31-0 .dropdown__menu-list::-webkit-scrollbar-thumb {\n    border-radius: 5px;\n    background-clip: padding-box;\n    background-color: rgba(160, 160, 160, 0.7);\n  }\n.dropdown__menu__dropdown__12-31-0 .dropdown__menu-list::-webkit-scrollbar-thumb:hover {\n    background-color: rgba(160, 160, 160, 1);\n  }\n.dropdown__menu__dropdown__12-31-0 .dropdown__menu-list::-webkit-scrollbar-corner {\n    background-color: rgba(160, 160, 160, 0.1);\n    border-bottom-right-radius: 5px;\n  }\n.dropdown__menu__dropdown__12-31-0 .dropdown__menu-notice {\n        padding: var(--SpaceS, 8px) var(--SpaceSM, 12px);\n        text-align: left;\n        height: 34px;\n      }\n.dropdown__menu__dropdown__12-31-0 .dropdown__menu-notice--no-options {\n          color: var(--FontColorLightGray, rgba(0, 0, 0, 0.45));\n        }\n.dropdown--error__dropdown__12-31-0 .dropdown__placeholder, .dropdown--error__dropdown__12-31-0:hover .dropdown__placeholder {\n        color: var(--FontColorError, #AE304A);\n      }\n.dropdown--error__dropdown__12-31-0 .dropdown__control, .dropdown--error__dropdown__12-31-0:hover .dropdown__control {\n        border-color: var(--FontColorError, #AE304A);\n        -webkit-box-shadow: var(--ControlBoxShadowFocusError, 0 0 0 3px rgba(174, 48, 74, 0.4));\n                box-shadow: var(--ControlBoxShadowFocusError, 0 0 0 3px rgba(174, 48, 74, 0.4));\n      }\n.dropdown--error__dropdown__12-31-0 .dropdown__control--menu-is-open, .dropdown--error__dropdown__12-31-0:hover .dropdown__control--menu-is-open {\n          -webkit-box-shadow: var(--ControlBoxShadowFocusDefault, 0 0 0 3px rgba(0, 126, 255, 0.2));\n                  box-shadow: var(--ControlBoxShadowFocusDefault, 0 0 0 3px rgba(0, 126, 255, 0.2));\n          border-color: var(--ControlBorderColorFocusDefault, #007EFF);\n          outline: none;\n        }\n.dropdown-placeholder--hotkey-hint__dropdown__12-31-0 {\n  text-align: right;\n}\n.multi-hint__line__dropdown__12-31-0 {\n    white-space: nowrap;\n  }\n";
var styles = {"dropdown-container":"dropdown-container__dropdown__12-31-0","dropdown-container--single-line":"dropdown-container--single-line__dropdown__12-31-0","dropdown__value-container--hidden-values":"dropdown__value-container--hidden-values__dropdown__12-31-0","dropdown__multi-hint":"dropdown__multi-hint__dropdown__12-31-0","dropdown__option-with-checkbox":"dropdown__option-with-checkbox__dropdown__12-31-0","dropdown__option-with-checkbox--is-focused":"dropdown__option-with-checkbox--is-focused__dropdown__12-31-0","dropdown__option-with-checkbox__label":"dropdown__option-with-checkbox__label__dropdown__12-31-0","dropdown__option-with-checkbox__checkbox":"dropdown__option-with-checkbox__checkbox__dropdown__12-31-0","dropdown__group-heading":"dropdown__group-heading__dropdown__12-31-0","dropdown__group-heading--is-disabled":"dropdown__group-heading--is-disabled__dropdown__12-31-0","dropdown__option-with-checkbox__checkbox--is-checked":"dropdown__option-with-checkbox__checkbox--is-checked__dropdown__12-31-0","dropdown__menu":"dropdown__menu__dropdown__12-31-0","dropdown--error":"dropdown--error__dropdown__12-31-0","dropdown-placeholder--hotkey-hint":"dropdown-placeholder--hotkey-hint__dropdown__12-31-0","multi-hint__line":"multi-hint__line__dropdown__12-31-0"};
styleInject(css);

var isMac = navigator.platform.startsWith('Mac');
var DefaultMultiSelectHint = React.createElement(React.Fragment, null, React.createElement("span", {
  className: styles['multi-hint__line']
}, "Hold ", React.createElement("b", null, isMac ? 'Cmd' : 'Ctrl'), " for multi-select."), ' ', React.createElement("span", {
  className: styles['multi-hint__line']
}, "Press ", React.createElement("b", null, isMac ? 'Cmd' : 'Ctrl', " + A"), " to select all."));
var OptionWithCheckbox = function OptionWithCheckbox(_ref) {
  var _classnames;
  var isChecked = _ref.isChecked,
    isDisabled = _ref.isDisabled,
    onCheckboxChange = _ref.onCheckboxChange,
    children = _ref.children,
    _ref$className = _ref.className,
    className = _ref$className === void 0 ? undefined : _ref$className;
  return React.createElement("div", {
    className: classnames(className, styles['dropdown__option-with-checkbox'])
  }, React.createElement("div", {
    className: styles['dropdown__option-with-checkbox__label']
  }, children), React.createElement("div", {
    className: classnames((_classnames = {}, _defineProperty(_classnames, styles['dropdown__option-with-checkbox__checkbox--is-disabled'], isDisabled), _defineProperty(_classnames, styles['dropdown__option-with-checkbox__checkbox--is-checked'], isChecked), _classnames), styles['dropdown__option-with-checkbox__checkbox'])
  }, React.createElement(Checkbox, {
    onChange: isDisabled ? undefined : onCheckboxChange,
    checked: isChecked,
    disabled: isDisabled
  })));
};
var getOption = function getOption(renderOption, className) {
  return function (props) {
    var data = props.data,
      isSelected = props.isSelected,
      isDisabled = props.isDisabled,
      innerProps = props.innerProps,
      getValue = props.getValue,
      _props$selectProps = props.selectProps,
      isMulti = _props$selectProps.isMulti,
      onChange = _props$selectProps.onChange,
      onInputChange = _props$selectProps.onInputChange,
      onMenuClose = _props$selectProps.onMenuClose,
      showCheckboxesIfMulti = _props$selectProps.showCheckboxesIfMulti;
    var updateValue = useCallback(function (shouldOverrideValue) {
      var shouldCloseMenuOnValueOverride = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
      if (shouldOverrideValue) {
        onChange([data], {
          action: 'set-value',
          option: data
        });
        if (shouldCloseMenuOnValueOverride) {
          onInputChange('');
          onMenuClose();
        }
      } else {
        var currentValue = getValue();
        var newValue = [];
        var action = null;
        if (isSelected) {
          newValue = currentValue.filter(function (option) {
            return option.id !== data.id;
          });
          action = 'deselect-option';
        } else {
          newValue = [].concat(_toConsumableArray(currentValue), [data]);
          action = 'select-option';
        }
        onChange(newValue, {
          action: action,
          option: data
        });
      }
    }, [getValue, onChange, isSelected, data]);
    var onMultiSelectOptionClick = useCallback(function (event) {
      var shouldOverrideValue = (!isMac || !event.metaKey) && (isMac || !event.ctrlKey);
      updateValue(shouldOverrideValue, true);
    }, [data, isSelected, getValue]);
    var onCheckboxChange = useCallback(function (_, event) {
      event.stopPropagation();
      updateValue(false);
    }, [isSelected, getValue, onChange, data]);
    var optionComponent = null;
    if (isMulti && showCheckboxesIfMulti && !data.hideCheckbox) {
      optionComponent = React.createElement(OptionWithCheckbox, {
        onCheckboxChange: onCheckboxChange,
        isDisabled: isDisabled,
        isChecked: isSelected
      }, renderOption(data, props.selectProps));
    } else {
      optionComponent = renderOption(data, props.selectProps);
    }
    return React.createElement(components.Option, Object.assign({}, props, {
      innerProps: isMulti && !isDisabled ? _extends({}, innerProps, {
        onClick: onMultiSelectOptionClick
      }) : innerProps,
      className: classnames(props.className, className)
    }), optionComponent);
  };
};
var MenuComponent = function MenuComponent(props) {
  return React.createElement(components.Menu, Object.assign({}, props, {
    className: classnames(styles['dropdown__menu'], props.selectProps.menuClassName)
  }));
};
var MenuListComponent = function MenuListComponent(props) {
  return React.createElement(React.Fragment, null, React.createElement(components.MenuList, Object.assign({}, props), props.children, props.selectProps.menuListFooter, props.isMulti && props.options && props.options.length > 1 && React.createElement("div", {
    className: styles['dropdown__multi-hint']
  }, props.selectProps.multiselectHint ? props.selectProps.multiselectHint : DefaultMultiSelectHint)));
};
var ControlComponent = function ControlComponent(props) {
  return React.createElement(components.Control, Object.assign({}, props, {
    className: props.selectProps.controlClassName
  }));
};
var InputComponent = function InputComponent(props) {
  var value = props.value,
    _props$selectProps2 = props.selectProps,
    onChange = _props$selectProps2.onChange,
    options = _props$selectProps2.options,
    filterOption = _props$selectProps2.filterOption,
    isOptionDisabled = _props$selectProps2.isOptionDisabled,
    isMulti = _props$selectProps2.isMulti,
    menuIsOpen = _props$selectProps2.menuIsOpen,
    getOptionValue = _props$selectProps2.getOptionValue,
    getOptionLabel = _props$selectProps2.getOptionLabel,
    inputPlaceholder = _props$selectProps2.inputPlaceholder;
  var handleKeyDown = useCallback(function (event) {
    if (menuIsOpen && isMulti && (isMac && event.metaKey || !isMac && event.ctrlKey) && event.key === 'a') {
      var filteredOptions = options.flatMap(function (option) {
        return Array.isArray(option.options) ? option.options : option;
      }).filter(function (option) {
        return !isOptionDisabled(option) && filterOption({
          label: getOptionLabel(option),
          value: getOptionValue(option),
          data: option
        }, value);
      });
      onChange(filteredOptions, {
        action: 'set-value'
      });
      event.preventDefault();
      event.stopPropagation();
    }
  }, [onChange, options, filterOption, isOptionDisabled, isMulti, menuIsOpen, value]);
  return React.createElement(components.Input, Object.assign({}, props, {
    onKeyDown: handleKeyDown,
    placeholder: menuIsOpen ? inputPlaceholder : undefined
  }));
};
var getMultiValueLabelComponent = function getMultiValueLabelComponent(renderValue) {
  return function (props) {
    return React.createElement(components.MultiValueLabel, Object.assign({}, props, {
      innerProps: _extends({}, props.innerProps, {
        className: classnames(props.innerProps.className, props.selectProps.multiValueLabelClassName)
      })
    }), renderValue(props.data, props.selectProps));
  };
};
var getSingleValueComponent = function getSingleValueComponent(renderValue) {
  return function (props) {
    if (props.selectProps.menuIsOpen && props.selectProps.inputPlaceholder) {
      return null;
    }
    return React.createElement(components.SingleValue, Object.assign({}, props), renderValue(props.data, props.selectProps));
  };
};
var ClearIndicator = function ClearIndicator(props) {
  return React.createElement(components.ClearIndicator, Object.assign({}, props), React.createElement(CloseIcon, {
    size: CloseIcon.Sizes.SMALL
  }));
};
var PlaceholderComponent = function PlaceholderComponent(props) {
  if (props.selectProps.menuIsOpen && props.selectProps.inputPlaceholder) {
    return null;
  }
  return React.createElement(components.Placeholder, Object.assign({}, props));
};
var MultiValueComponent = function MultiValueComponent(props) {
  if (props.selectProps.menuIsOpen && (props.selectProps.inputPlaceholder || props.selectProps.singleLine)) {
    return null;
  }
  return React.createElement(components.MultiValue, Object.assign({}, props));
};
var MultiValueRemove = function MultiValueRemove(props) {
  return props.selectProps.isOptionDisabled(props.data) || !props.selectProps.showMultiValueRemove ? null : React.createElement(components.MultiValueRemove, Object.assign({}, props), React.createElement(CloseIcon, {
    color: Third900,
    size: CloseIcon.Sizes.SMALL
  }));
};
var DropdownIndicator = function DropdownIndicator(props) {
  return React.createElement(components.DropdownIndicator, Object.assign({}, props), React.createElement(CaretIcon, {
    direction: CaretIcon.Directions.BOTTOM,
    size: CaretIcon.Sizes.SMALL
  }));
};
var ValueContainerComponent = function ValueContainerComponent(props) {
  return React.createElement(components.ValueContainer, Object.assign({}, props, {
    className: classnames(props.className, props.selectProps.valueContainerClassName, _defineProperty({}, styles['dropdown__value-container--hidden-values'], props.selectProps.menuIsOpen && props.selectProps.isMulti && (props.selectProps.inputPlaceholder || props.selectProps.singleLine)))
  }));
};
var GroupComponent = function GroupComponent(props) {
  var options = props.options,
    Heading = props.Heading,
    getValue = props.getValue;
  var WrappedHeading = useMemo(function () {
    return function (pr) {
      return React.createElement(Heading, Object.assign({}, pr, {
        options: options,
        getValue: getValue
      }));
    };
  }, [options, getValue, Heading]);
  return React.createElement(components.Group, Object.assign({}, props, {
    Heading: WrappedHeading,
    className: classnames(props.className, props.selectProps.groupClassName)
  }));
};
var GroupHeadingComponent = function GroupHeadingComponent(_ref2) {
  var options = _ref2.options,
    getValue = _ref2.getValue,
    props = _objectWithoutProperties(_ref2, ["options", "getValue"]);
  var data = props.data,
    _props$selectProps3 = props.selectProps,
    onChange = _props$selectProps3.onChange,
    onInputChange = _props$selectProps3.onInputChange,
    onMenuClose = _props$selectProps3.onMenuClose,
    isMulti = _props$selectProps3.isMulti,
    showCheckboxesIfMulti = _props$selectProps3.showCheckboxesIfMulti,
    formatGroupLabel = _props$selectProps3.formatGroupLabel,
    groupHeadingClassName = _props$selectProps3.groupHeadingClassName;
  var possibleOptions = useMemo(function () {
    return options.filter(function (option) {
      return !option.isDisabled;
    });
  }, [options]);
  var notSelectedOptions = useMemo(function () {
    return possibleOptions.filter(function (option) {
      return !option.isSelected;
    });
  }, [possibleOptions]);
  var isDisabled = !isMulti || possibleOptions.length === 0;
  var isSelected = notSelectedOptions.length === 0 && possibleOptions.length > 0;
  var updateGroupValue = useCallback(function (shouldOverrideValue) {
    var shouldCloseMenuOnValueOverride = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
    if (shouldOverrideValue) {
      onChange(possibleOptions.map(function (option) {
        return option.data;
      }), {
        action: 'set-value'
      });
      if (shouldCloseMenuOnValueOverride) {
        onInputChange('');
        onMenuClose();
      }
    } else {
      var currentValue = getValue();
      var notSelectedMappedOptions = notSelectedOptions.map(function (option) {
        return option.data;
      });
      var newValue = [];
      if (notSelectedMappedOptions.length === 0) {
        var groupOptionIds = options.map(function (op) {
          return op.data.id;
        });
        newValue = _toConsumableArray(currentValue.filter(function (option) {
          return !groupOptionIds.includes(option.id);
        }));
      } else {
        newValue = [].concat(_toConsumableArray(currentValue), _toConsumableArray(notSelectedMappedOptions));
      }
      onChange(newValue, {
        action: 'set-value'
      });
    }
  }, [getValue, options, possibleOptions, notSelectedOptions, isMac]);
  var onClick = useCallback(function (event) {
    var shouldOverrideValue = (!isMac || !event.metaKey) && (isMac || !event.ctrlKey);
    updateGroupValue(shouldOverrideValue, true);
  }, [updateGroupValue]);
  var onCheckboxChange = useCallback(function (_, event) {
    event.stopPropagation();
    updateGroupValue(false);
  }, [updateGroupValue]);
  var label = formatGroupLabel(data);
  var optionComponent = null;
  if (isMulti && showCheckboxesIfMulti && !data.hideCheckbox) {
    optionComponent = React.createElement(OptionWithCheckbox, {
      onCheckboxChange: onCheckboxChange,
      isDisabled: isDisabled,
      isChecked: isSelected
    }, label);
  } else {
    optionComponent = label;
  }
  return React.createElement("div", {
    className: classnames(_defineProperty({}, styles['dropdown__group-heading--is-disabled'], isDisabled), styles['dropdown__group-heading'], groupHeadingClassName),
    onClick: isDisabled ? undefined : onClick
  }, optionComponent);
};
var MultiValueContainerComponent = function MultiValueContainerComponent(props) {
  return React.createElement(components.MultiValueContainer, Object.assign({}, props, {
    innerProps: _extends({}, props.innerProps, {
      className: classnames(props.innerProps.className, props.selectProps.multiValueContainerClassName)
    })
  }));
};

var _kindsClassMapping;
var defaultRenderValue = function defaultRenderValue(option) {
  return option.label;
};
var defaultIsDisabled = function defaultIsDisabled(option) {
  return option.disabled;
};
var defaultGetOptionValue = function defaultGetOptionValue(option) {
  return option.id;
};
var MenuPlacements = {
  AUTO: 'auto',
  BOTTOM: 'bottom',
  TOP: 'top'
};
var ChangeActions = {
  SET_VALUE: 'set-value',
  REMOVE_VALUE: 'remove-value',
  SELECT_OPTION: 'select-option',
  DESELECT_OPTION: 'deselect-option',
  CLEAR: 'clear'
};
var DropdownKinds = {
  "default": 'default',
  error: 'error'
};
var kindsClassMapping = (_kindsClassMapping = {}, _defineProperty(_kindsClassMapping, DropdownKinds["default"], undefined), _defineProperty(_kindsClassMapping, DropdownKinds.error, 'dropdown--error'), _kindsClassMapping);
var Dropdown = /*#__PURE__*/function (_React$Component) {
  _inherits(Dropdown, _React$Component);
  function Dropdown() {
    var _this;
    _classCallCheck(this, Dropdown);
    _this = _possibleConstructorReturn(this, _getPrototypeOf(Dropdown).apply(this, arguments));
    _this.suppressCloseOnScroll = false;
    _this.handleMenuOpen = function () {
      // Temporarily suppress closing the menu on scroll to avoid closing right after opening
      // in case dropdown menu changes screen size and triggers document scroll event
      _this.suppressCloseOnScroll = true;
      clearTimeout(_this.suppressCloseOnScrollTimer);
      _this.suppressCloseOnScrollTimer = setTimeout(function () {
        _this.suppressCloseOnScroll = false;
      }, 0);
      if (_this.props.onMenuOpen) {
        _this.props.onMenuOpen();
      }
    };
    return _this;
  }
  _createClass(Dropdown, [{
    key: "render",
    value: function render() {
      var _this2 = this;
      var _this$props = this.props,
        isMulti = _this$props.isMulti,
        isClearable = _this$props.isClearable,
        closeMenuOnSelect = _this$props.closeMenuOnSelect,
        hideSelectedOptions = _this$props.hideSelectedOptions,
        menuZIndex = _this$props.menuZIndex,
        minMenuHeight = _this$props.minMenuHeight,
        className = _this$props.className,
        placeholder = _this$props.placeholder,
        getNoOptionsMessage = _this$props.getNoOptionsMessage,
        value = _this$props.value,
        options = _this$props.options,
        getOptionValue = _this$props.getOptionValue,
        isDisabled = _this$props.isDisabled,
        isOptionDisabled = _this$props.isOptionDisabled,
        menuPlacement = _this$props.menuPlacement,
        menuPortalTarget = _this$props.menuPortalTarget,
        menuClassName = _this$props.menuClassName,
        controlClassName = _this$props.controlClassName,
        groupClassName = _this$props.groupClassName,
        groupHeadingClassName = _this$props.groupHeadingClassName,
        valueContainerClassName = _this$props.valueContainerClassName,
        onChange = _this$props.onChange,
        renderSingleValue = _this$props.renderSingleValue,
        renderMultiValue = _this$props.renderMultiValue,
        renderOption = _this$props.renderOption,
        kind = _this$props.kind,
        showMultiValueRemove = _this$props.showMultiValueRemove,
        multiValueContainerClassName = _this$props.multiValueContainerClassName,
        optionClassName = _this$props.optionClassName,
        singleLine = _this$props.singleLine,
        renderIndicator = _this$props.renderIndicator,
        otherProps = _objectWithoutProperties(_this$props, ["isMulti", "isClearable", "closeMenuOnSelect", "hideSelectedOptions", "menuZIndex", "minMenuHeight", "className", "placeholder", "getNoOptionsMessage", "value", "options", "getOptionValue", "isDisabled", "isOptionDisabled", "menuPlacement", "menuPortalTarget", "menuClassName", "controlClassName", "groupClassName", "groupHeadingClassName", "valueContainerClassName", "onChange", "renderSingleValue", "renderMultiValue", "renderOption", "kind", "showMultiValueRemove", "multiValueContainerClassName", "optionClassName", "singleLine", "renderIndicator"]);
      return React.createElement(Select, Object.assign({
        filterOption: createFilter({
          ignoreAccents: false
        }),
        menuClassName: menuClassName,
        controlClassName: controlClassName,
        valueContainerClassName: valueContainerClassName,
        multiValueContainerClassName: multiValueContainerClassName,
        groupClassName: groupClassName,
        groupHeadingClassName: groupHeadingClassName,
        className: classnames(styles['dropdown-container'],
        // hasOwnProperty explicitly checked here
        // eslint-disable-next-line security/detect-object-injection
        kindsClassMapping.hasOwnProperty(kind) && styles[kindsClassMapping[kind]], className, _defineProperty({}, styles['dropdown-container--single-line'], singleLine)),
        classNamePrefix: "dropdown",
        noOptionsMessage: getNoOptionsMessage,
        placeholder: placeholder,
        isMulti: isMulti,
        isClearable: isClearable,
        components: {
          SingleValue: getSingleValueComponent(renderSingleValue),
          MultiValueLabel: getMultiValueLabelComponent(renderMultiValue),
          MultiValueContainer: MultiValueContainerComponent,
          MultiValueRemove: MultiValueRemove,
          DropdownIndicator: renderIndicator,
          Option: getOption(renderOption, optionClassName),
          Menu: MenuComponent,
          MenuList: MenuListComponent,
          Control: ControlComponent,
          Group: GroupComponent,
          GroupHeading: GroupHeadingComponent,
          ClearIndicator: ClearIndicator,
          ValueContainer: ValueContainerComponent,
          Input: InputComponent,
          MultiValue: MultiValueComponent,
          Placeholder: PlaceholderComponent
        },
        closeMenuOnSelect: closeMenuOnSelect,
        hideSelectedOptions: hideSelectedOptions,
        value: value,
        onChange: onChange,
        options: options,
        getOptionValue: getOptionValue,
        isDisabled: isDisabled,
        isOptionDisabled: isOptionDisabled,
        showMultiValueRemove: showMultiValueRemove,
        menuPlacement: menuPlacement,
        menuPortalTarget: menuPortalTarget,
        styles: {
          menuPortal: function menuPortal(base) {
            return _extends({}, base, {
              zIndex: menuZIndex
            });
          },
          menu: function menu(base) {
            return _extends({}, base, {
              border: "1px solid var(--ControlBorderColorDefault, ".concat(ControlBorderColorDefault, ")"),
              borderRadius: "var(--ControlBorderRadiusDefault, ".concat(ControlBorderRadiusDefault, ")"),
              boxShadow: 'none'
            });
          }
        },
        minMenuHeight: minMenuHeight,
        backspaceRemovesValue: false,
        singleLine: singleLine,
        closeMenuOnScroll: function closeMenuOnScroll(scrollEvent) {
          var _a, _b;
          if (_this2.suppressCloseOnScroll) {
            return false;
          }
          // check if scroll event is coming from within my parent -
          // - in this case, do not close to allow scrolling within the dropdown
          return scrollEvent.target && !(((_a = scrollEvent.target.classList) === null || _a === void 0 ? void 0 : _a.contains('dropdown__menu-list')) || ((_b = scrollEvent.target.classList) === null || _b === void 0 ? void 0 : _b.contains('dropdown__value-container')));
        }
      }, otherProps, {
        onMenuOpen: this.handleMenuOpen
      }));
    }
  }]);
  return Dropdown;
}(React.Component);
Dropdown.MenuPlacements = MenuPlacements;
Dropdown.ChangeActions = ChangeActions;
Dropdown.Kinds = DropdownKinds;
Dropdown.defaultProps = {
  menuZIndex: 100001,
  minMenuHeight: 250,
  isMulti: false,
  isClearable: false,
  closeMenuOnSelect: false,
  hideSelectedOptions: false,
  getOptionValue: defaultGetOptionValue,
  renderSingleValue: defaultRenderValue,
  renderMultiValue: defaultRenderValue,
  renderOption: defaultRenderValue,
  isDisabled: false,
  isOptionDisabled: defaultIsDisabled,
  menuPlacement: MenuPlacements.AUTO,
  menuPortalTarget: document.body,
  kind: 'default',
  showMultiValueRemove: true,
  singleLine: false,
  showCheckboxesIfMulti: false,
  renderIndicator: DropdownIndicator
};

/*********************************************************************
 * © Copyright IBM Corp. 2024
 *********************************************************************/

export { Dropdown };
